import React, { CSSProperties, Fragment, useState } from 'react';
import { at, get, isEmpty, omit, toString } from 'lodash';
import { LoadingWrapper } from '@ardoq/status-ui';
import * as BasicTable from './BasicTable';
import { EmptyTablePage as EmptyTableBanner } from './EmptyTablePage';
import {
  Cell,
  Column as ColumnType,
  DataSourceTableProps,
  EnhanceProps,
  HeaderCell,
  Row,
  RowData,
  TableComponents,
} from './types';
import { reduceWithColSpan } from './utils';

type Column<TData extends RowData> = ColumnType<TData>;

const Colgroup = (props: { headerCells: HeaderCell[] }) => {
  return (
    <colgroup>
      {props.headerCells.map((headerCell, i) => (
        <col
          key={i}
          style={{
            width: headerCell.style.width,
            maxWidth: headerCell.style.width,
          }}
        />
      ))}
    </colgroup>
  );
};

const defaultValueRender = toString;

const composeCellData = <TData extends RowData>(
  dataSourceRow: TData,
  columnConfig: Column<TData>
): Cell<TData> => {
  const value = at(dataSourceRow as any, columnConfig.dataIndex || '')[0];
  return {
    value,
    dataSourceRow,
    style:
      columnConfig.getCellStyle?.(dataSourceRow) ||
      columnConfig.cellStyle ||
      {},
    valueRender: columnConfig.valueRender || defaultValueRender,
    getColSpan: columnConfig.getColSpan,
    onContextClick: columnConfig.onContextClick
      ? (event: React.MouseEvent) =>
          columnConfig.onContextClick &&
          columnConfig.onContextClick(dataSourceRow, event)
      : undefined,
    onCellClick: columnConfig.onCellClick
      ? (
          event: React.MouseEvent // this check is addional, it's made just to TS ;)
        ) => {
          if (event.shiftKey && columnConfig.onShiftClick) {
            event.stopPropagation();
            return columnConfig.onShiftClick(dataSourceRow, event);
          }
          return (
            columnConfig.onCellClick &&
            columnConfig.onCellClick(dataSourceRow, event)
          );
        }
      : undefined,
  };
};

const mapDatasourceToRowData = <TData extends RowData>(
  rowData: TData,
  columns: Column<TData>[]
): Row<TData> => columns.map(column => composeCellData(rowData, column));

const mapColumnToHeaderCell = <TData extends RowData>(
  column: Column<TData>
): HeaderCell => ({
  headerRender: column.headerRender,
  title: column.title || '',
  style: column.headerStyle || {},
  sortOrder: column.sortOrder ? column.sortOrder : undefined,
  onClick: column.onHeaderClick,
  dataIndex: column.dataIndex,
});

type DataSourceTableHeaderProps = {
  fixedHeader?: boolean;
  showShadow?: boolean;
  components: TableComponents;
  headerCells: HeaderCell[];
};

const DataSourceTableHeader = (props: DataSourceTableHeaderProps) => {
  const { components, headerCells, fixedHeader, showShadow } = props;
  const { Thead, HeadTr, SortIcon, Th, ThBody } = components;
  return (
    <Thead
      className={showShadow ? 'show-shadow' : ''}
      style={
        fixedHeader
          ? {
              top: 0,
              position: 'sticky',
              zIndex: 1,
            }
          : undefined
      }
    >
      <HeadTr>
        {headerCells.map((headerCell, index) => (
          <Th
            key={index}
            style={omit(headerCell.style, 'width', 'maxWidth')}
            onClick={headerCell.onClick}
          >
            <ThBody>
              {headerCell.headerRender ? (
                headerCell.headerRender(headerCell)
              ) : (
                <>
                  {headerCell.title}
                  {headerCell.sortOrder && (
                    <SortIcon order={headerCell.sortOrder} />
                  )}
                </>
              )}
            </ThBody>
          </Th>
        ))}
      </HeadTr>
    </Thead>
  );
};

const getRowId = <TData extends RowData, TKey extends keyof TData>(
  row?: TData,
  dataIndex?: TKey
): string | null => (row ? (get(row, dataIndex || '_id') as string) : null);

const generatePseudoUniqKey = (
  values: Array<string | number | null | undefined>
): string => {
  return values.filter(value => value).join('-');
};

const defaultRowStyle = () => ({});

/** @deprecated Use `@ardoq/table` instead. */
const DatasourceTable = <TData extends RowData>(
  _props: DataSourceTableProps<TData>
) => {
  const props = enhanceProps(_props);
  const {
    fixedHeader,
    isFixedLayout,
    components,
    headerCells,
    idIndex = '_id',
    dragDropProps = {},
    onTableClick,
    onContextMenu,
    onRowClick,
    getRowComponent,
    loading,
    className,
    onScroll,
    scrollableSectionHeight,
    style,
    dataSource,
    getExpandedRow,
    expandedRowIds = [],
    topPlaceholderHeight = 0,
    bottomPlaceholderHeight = 0,
    tfootContent,
    onKeyDown,
    tableName,
    hideHeader,
    rowDataToRowAriaLabel,
    ariaLabel,
  } = props;
  const [showShadow, setShowShadow] = useState<boolean>(false);
  const { Table, BodyTr, Td, Tbody, Tfoot } = components;
  const rowStyle = props.rowStyle || defaultRowStyle;

  const tableStyle: CSSProperties = {
    ...(fixedHeader ? { position: 'relative' } : {}),
    ...(isFixedLayout ? { tableLayout: 'fixed' } : {}),
    ...(dataSource.length === 0 && props.renderEmptyTable
      ? { height: '100%' } // full height for empty table doqy
      : {}),
  };

  return (
    <LoadingWrapper
      loading={loading}
      className={className}
      onScroll={e => {
        onScroll?.(e);
        setShowShadow(e.currentTarget.scrollTop > 5);
      }}
      style={{
        overflowY: 'auto',
        height: scrollableSectionHeight,
        ...(style || {}),
      }}
    >
      <Table
        onClick={onTableClick}
        onKeyDown={onKeyDown}
        onContextMenu={onContextMenu ?? onTableClick}
        $renameId={dragDropProps?.renameId}
        style={tableStyle}
        aria-label={ariaLabel}
      >
        {!hideHeader && (
          <>
            <Colgroup headerCells={headerCells} />
            <DataSourceTableHeader
              showShadow={showShadow}
              fixedHeader={fixedHeader}
              headerCells={headerCells}
              components={components}
            />
          </>
        )}
        <Tbody data-test-id="table-body">
          {topPlaceholderHeight > 0 ? (
            <tr style={{ height: topPlaceholderHeight }}>
              <td />
            </tr>
          ) : null}
          {props.rows.map((row: Row<TData>, rowIndex: number) => {
            const rowData = dataSource[rowIndex];
            const isRowExpanded = expandedRowIds.includes(
              getRowId(rowData, idIndex as keyof TData) ?? ''
            );
            const RowComponent =
              getRowComponent?.(dataSource[rowIndex]) ?? BodyTr;

            // these are used for creating draggable BodyTr not needed/used for default <tr /> element
            const draggableBodyTrProps = {
              dragDropProps,
              dataSourceRow: rowData,
              index: rowIndex,
            };
            return (
              <Fragment
                key={
                  getRowId(rowData, idIndex as keyof TData) ??
                  generatePseudoUniqKey([tableName, 'row', rowIndex])
                }
              >
                <RowComponent
                  $even={rowIndex % 2}
                  style={rowStyle(rowData)}
                  onClick={(e: React.MouseEvent) =>
                    onRowClick && onRowClick(rowData, e)
                  }
                  aria-label={rowDataToRowAriaLabel?.(rowData)}
                  data-row-id={getRowId(rowData, idIndex as keyof TData)}
                  data-test-id="table-row"
                  {...(!isEmpty(dragDropProps) && draggableBodyTrProps)}
                >
                  {reduceWithColSpan(row).map(
                    (
                      {
                        value,
                        valueRender,
                        dataSourceRow,
                        style,
                        onCellClick,
                        onContextClick,
                        getColSpan,
                      },
                      cellIndex
                    ) => (
                      <Td
                        key={generatePseudoUniqKey([
                          tableName,
                          'row',
                          rowIndex,
                          'cell',
                          cellIndex,
                          'value',
                          value,
                        ])}
                        style={style}
                        colSpan={
                          getColSpan
                            ? getColSpan(value, dataSourceRow)
                            : undefined
                        }
                        onClick={onCellClick}
                        onContextMenu={onContextClick}
                      >
                        {valueRender(value, dataSourceRow)}
                      </Td>
                    )
                  )}
                </RowComponent>
                {getExpandedRow && isRowExpanded && (
                  <tr>
                    <td colSpan={row.length}>{getExpandedRow(rowData)}</td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {props.emptyTable()}
          {bottomPlaceholderHeight > 0 ? (
            <tr style={{ height: bottomPlaceholderHeight }}>
              <td />
            </tr>
          ) : null}
        </Tbody>
        {tfootContent ? <Tfoot>{tfootContent}</Tfoot> : null}
      </Table>
    </LoadingWrapper>
  );
};

const enhanceProps = <TData extends RowData>(
  props: DataSourceTableProps<TData>
): EnhanceProps<TData> & DataSourceTableProps<TData> => ({
  ...props,
  components: {
    Table: BasicTable.Table,
    Tr: BasicTable.Tr,
    Th: BasicTable.Th,
    Thead: BasicTable.Thead,
    Tbody: BasicTable.Tbody,
    Tfoot: BasicTable.Tfoot,
    Td: BasicTable.Td,
    SortIcon: BasicTable.SortIcon,
    ScrollableBody: BasicTable.ScrollableBody,
    BodyTr: BasicTable.Tr,
    HeadTr: BasicTable.Tr,
    ThBody: Fragment,
    ...props.components,
  },
  headerCells: props.columns.map(mapColumnToHeaderCell),
  emptyTable: () =>
    props.dataSource.length === 0 && props.renderEmptyTable ? (
      <BasicTable.Tr>
        <BasicTable.Td colSpan={props.columns.length}>
          {typeof props.renderEmptyTable === 'object' ? (
            <EmptyTableBanner
              title={props.renderEmptyTable.title}
              subtitle={props.renderEmptyTable.subtitle}
            />
          ) : (
            props.renderEmptyTable()
          )}
        </BasicTable.Td>
      </BasicTable.Tr>
    ) : null,
  rows: props.dataSource.map(dataSourceRow =>
    mapDatasourceToRowData(dataSourceRow, props.columns)
  ),
});

export { DatasourceTable };
